import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaEye, FaEyeSlash } from "react-icons/fa";

const API = process.env.REACT_APP_API_URL || "http://localhost:5000";

export default function Login() {
    const navigate = useNavigate();
    const [formData, setFormData] = useState({ email: "", password: "" });
    const [loading, setLoading] = useState(false);
    const [focused, setFocused] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [loginRole, setLoginRole] = useState("patient"); // 'patient' | 'doctor' | 'admin'
    const [otp, setOtp] = useState("");
    const [otpSent, setOtpSent] = useState(false);
    const [timer, setTimer] = useState(60);
    const [canResend, setCanResend] = useState(false);
    const [phoneLogin, setPhoneLogin] = useState(false);
const [phone, setPhone] = useState("");
const [phoneOtp, setPhoneOtp] = useState("");
const [phoneOtpSent, setPhoneOtpSent] = useState(false);

    useEffect(() => {
        // clear form when switching role
        setFormData({ email: "", password: "" });

        // also reset OTP flow
        setOtp("");
        setOtpSent(false);
        setTimer(60);
        setCanResend(false);
    }, [loginRole]);

    useEffect(() => {
        let interval;

        if (otpSent && timer > 0) {
            interval = setInterval(() => {
                setTimer((prev) => prev - 1);
            }, 1000);
        }

        if (timer === 0) {
            setCanResend(true);
        }

        return () => clearInterval(interval);
    }, [otpSent, timer]);

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };
    // Add this handler inside Login component
    const handleGoogleLogin = () => {
        window.location.href = `${API}api/auth/google`;
    };
    // Add same getLocation function inside Login component

    const handleLogin = async (e) => {
        e.preventDefault();

        // 👉 ADMIN FLOW (OTP — same as doctor)
        if (loginRole === "admin") {
            try {
                setLoading(true);
                const res = await fetch(`${API}/api/admin/send-otp`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(formData),
                });
                const data = await res.json();
                setLoading(false);
                if (!res.ok) return toast.error(data.message);
                setOtpSent(true);
                toast.success("OTP sent to your admin email");
            } catch (err) {
                setLoading(false);
                toast.error("Failed to send OTP");
            }
            return;
        }

        // 👉 DOCTOR FLOW (OTP)
        if (loginRole === "doctor") {
            try {
                setLoading(true);

                // Step 1: Verify email + password
                const res = await fetch(`${API}/api/doctor-portal/login`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(formData),
                });

                const data = await res.json();

                if (!res.ok) {
                    setLoading(false);
                    return toast.error(data.message);
                }

                // Step 2: Send OTP
                await fetch(`${API}/api/doctor-portal/send-otp`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ email: formData.email }),
                });

                setOtpSent(true);
                setLoading(false);
                toast.success("OTP sent to your email");

            } catch (err) {
                setLoading(false);
                toast.error("Failed to send OTP");
            }

            return;
        }

        // 👉 PATIENT FLOW (normal login)
        try {
            setLoading(true);

            const res = await fetch(`${API}/api/auth/login`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(formData),
            });

            const data = await res.json();
            setLoading(false);

            if (res.ok) {
                localStorage.setItem("user", JSON.stringify(data.user));
                toast.success("Patient Login successful!");
                setTimeout(() => navigate("/doctors"), 1500);
            } else {
                toast.error(data.message);
            }
        } catch (err) {
            setLoading(false);
            toast.error("Failed to login");
        }
    };
    const handleVerifyOtp = async () => {
        try {
            setLoading(true);

            // Admin OTP verify
            if (loginRole === "admin") {
                const res = await fetch(`${API}/api/admin/verify-otp`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ email: formData.email, otp }),
                });
                const data = await res.json();
                setLoading(false);
                if (res.ok) {
                    localStorage.setItem("adminToken", data.token);
                    toast.success("Admin Login successful!");
                    setTimeout(() => navigate("/admin/dashboard"), 1500);
                } else {
                    toast.error(data.message);
                }
                return;
            }

            // Doctor OTP verify
            const res = await fetch(`${API}/api/doctor-portal/verify-otp`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    email: formData.email,
                    otp,
                }),
            });

            const data = await res.json();
            setLoading(false);

            if (res.ok) {
                localStorage.setItem("doctor", JSON.stringify(data.doctor));
                toast.success("Doctor Login successful!");
                setTimeout(() => navigate("/doctor-portal/appointments"), 1500);
            } else {
                toast.error(data.message);
            }
        } catch (err) {
            setLoading(false);
            toast.error("OTP verification failed");
        }
    };

    const handleResendOtp = async () => {
        try {
            setLoading(true);
            const endpoint = loginRole === "admin"
                ? `${API}/api/admin/send-otp`
                : `${API}/api/doctor-portal/send-otp`;

            await fetch(endpoint, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(formData),
            });

            setTimer(60);
            setCanResend(false);
            setLoading(false);
            toast.success("OTP resent successfully");
        } catch (err) {
            setLoading(false);
            toast.error("Failed to resend OTP");
        }
    };
    const handleSendPhoneOtp = async () => {
    try {
        setLoading(true);

        const res = await fetch(`${API}/api/auth/send-phone-otp`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                phone,
            }),
        });

        const data = await res.json();

        setLoading(false);

        if (res.ok) {
            setPhoneOtpSent(true);
            toast.success("OTP sent successfully");
        } else {
            toast.error(data.message);
        }

    } catch (err) {
        setLoading(false);
        toast.error("Failed to send OTP");
    }
};

const handleVerifyPhoneOtp = async () => {
    try {
        setLoading(true);

        const res = await fetch(`${API}/api/auth/verify-phone-otp`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                phone,
                otp: phoneOtp,
            }),
        });

        const data = await res.json();

        setLoading(false);

        if (res.ok) {
            localStorage.setItem("user", JSON.stringify(data.user));
            toast.success("Login Successful");
            navigate("/doctors");
        } else {
            toast.error(data.message);
        }

    } catch (err) {
        setLoading(false);
        toast.error("OTP verification failed");
    }
};

    return (
        <>
            <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;0,600;1,600&family=DM+Sans:wght@300;400;500&display=swap');
        .font-cormorant { font-family: 'Cormorant Garamond', serif; }
        .font-dm { font-family: 'DM Sans', sans-serif; }
        @keyframes spin { to { transform: rotate(360deg); } }
        .spinner {
          display: inline-block; width: 16px; height: 16px;
          border: 2px solid rgba(255,255,255,0.4); border-top-color: #fff;
          border-radius: 50%; animation: spin 0.7s linear infinite;
          vertical-align: middle; margin-right: 8px;
        }
        .auth-input:focus { box-shadow: 0 0 0 3px rgba(26,111,168,0.1); }
      `}</style>

            <div className="font-dm min-h-screen flex">

                {/* Left Panel */}
                <div
                    className="hidden md:flex w-[45%] flex-col justify-between p-12 relative overflow-hidden"
                    style={{ background: "linear-gradient(145deg,#0a3d62 0%,#1a6fa8 50%,#0d8f8f 100%)" }}
                >
                    <div className="absolute w-[500px] h-[500px] rounded-full border border-white/[0.08] -top-36 -left-36 pointer-events-none" />
                    <div className="absolute w-[300px] h-[300px] rounded-full border border-white/[0.06] bottom-20 -right-20 pointer-events-none" />

                    {/* Brand */}
                    <div className="flex items-center gap-3 z-10">
                        <div className="w-11 h-11 bg-white/15 rounded-xl flex items-center justify-center text-2xl">🏥</div>
                        <span className="font-cormorant text-[22px] font-semibold text-white tracking-wide">MediBook</span>
                    </div>

                    {/* Headline */}
                    <div className="z-10">
                        <h1 className="font-cormorant text-[46px] font-light text-white leading-[1.2] mb-5">
                            Your health,<br />
                            <span className="font-semibold italic">expertly</span><br />
                            managed.
                        </h1>
                        <p className="text-white/65 text-[15px] leading-relaxed font-light max-w-xs">
                            Book appointments with top-rated doctors in your area. Seamless, secure, and always available.
                        </p>
                        {/* Stats */}
                        <div className="flex gap-8 mt-12">
                            {[["500+", "Doctors"], ["50k+", "Patients"], ["4.9★", "Rating"]].map(([num, label]) => (
                                <div key={label}>
                                    <div className="font-cormorant text-[32px] font-medium leading-none text-white">{num}</div>
                                    <div className="text-[11px] text-white/55 mt-1 uppercase tracking-wide">{label}</div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Decorative cross */}
                    <svg className="absolute bottom-40 right-12 z-10 opacity-[0.12]" width="120" height="120" viewBox="0 0 120 120" fill="none">
                        <rect x="45" y="0" width="30" height="120" rx="15" fill="white" />
                        <rect x="0" y="45" width="120" height="30" rx="15" fill="white" />
                    </svg>

                    <p className="z-10 text-white/35 text-xs tracking-wide">© 2025 MediBook · All rights reserved</p>
                </div>

                {/* Right Panel */}
                <div className="flex-1 flex items-center justify-center px-8 py-12 bg-slate-50">
                    <div className="w-full max-w-sm">
                        <h2 className="font-cormorant text-[36px] font-medium text-[#0a3d62] mb-1">
                            {loginRole === "doctor" ? "Doctor Portal" : loginRole === "admin" ? "Admin Portal" : "Welcome back"}
                        </h2>
                        <p className="text-slate-400 text-sm font-light mb-7">
                            {loginRole === "doctor" ? "Sign in to manage your practice" : loginRole === "admin" ? "Sign in to manage the platform" : "Sign in to manage your appointments"}
                        </p>

                        {/* Role Toggle */}
                        <div className="flex p-1 mb-8 bg-slate-100 rounded-xl">
                            <button
                                type="button"
                                onClick={() => setLoginRole("patient")}
                                className={`flex-1 py-2 text-[13px] font-medium rounded-lg transition-all duration-200 ${loginRole === "patient" ? "bg-white text-[#1a6fa8] shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
                            >
                                Patient
                            </button>
                            <button
                                type="button"
                                onClick={() => setLoginRole("doctor")}
                                className={`flex-1 py-2 text-[13px] font-medium rounded-lg transition-all duration-200 ${loginRole === "doctor" ? "bg-white text-[#1a6fa8] shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
                            >
                                Doctor/Therapist
                            </button>
                            <button
                                type="button"
                                onClick={() => setLoginRole("admin")}
                                className={`flex-1 py-2 text-[13px] font-medium rounded-lg transition-all duration-200 ${loginRole === "admin" ? "bg-white text-[#1a6fa8] shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
                            >
                                Admin
                            </button>
                        </div>

                        {/* Google Login FIRST */}
                        {loginRole === "patient" && (
                            <button
                                onClick={handleGoogleLogin}
                                className="w-full flex items-center justify-center gap-3 py-3 rounded-xl border-[1.5px] border-slate-200 bg-white text-slate-700 text-[14px] font-medium transition-all duration-200 hover:bg-slate-50 hover:shadow-md mb-6"
                            >
                                <svg width="18" height="18" viewBox="0 0 48 48">
                                    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
                                    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
                                    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
                                    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
                                </svg>
                                Continue with Google
                            </button>
                        )}
                        <button
    type="button"
    onClick={() => {
        setPhoneLogin(true);
        setPhoneOtpSent(false);
        setPhone("");
        setPhoneOtp("");
    }}
    className="w-full flex items-center justify-center gap-3 py-3 rounded-xl border-[1.5px] border-slate-200 bg-white text-slate-700 text-[14px] font-medium transition-all duration-200 hover:bg-slate-50 hover:shadow-md mt-3"
>
    📱 Continue with Phone Number
</button>

{phoneLogin && (
    <div className="mb-6">

        {/* Back Button */}
        

        {/* Phone Number */}
        <label className="block text-[11px] font-medium text-slate-500 uppercase tracking-wide mb-2 mt-5">
            Enter Phone Number
        </label>

        <input
            type="tel"
            placeholder="+91 9876543210"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            className="auth-input w-full px-4 py-3 border rounded-xl mb-3"
        />

        {!phoneOtpSent ? (
            <button
                type="button"
                onClick={handleSendPhoneOtp}
                className="w-full py-3 rounded-xl text-white"
                style={{
                    background: "linear-gradient(135deg,#0a3d62,#1a6fa8)"
                }}
            >
                Send OTP
            </button>
        ) : (
            <>
                <input
                    type="text"
                    placeholder="Enter OTP"
                    value={phoneOtp}
                    onChange={(e) => setPhoneOtp(e.target.value)}
                    className="auth-input w-full px-4 py-3 border rounded-xl mt-3"
                />

                <button
                    type="button"
                    onClick={handleVerifyPhoneOtp}
                    className="w-full mt-3 py-3 rounded-xl text-white"
                    style={{
                        background: "linear-gradient(135deg,#0a3d62,#1a6fa8)"
                    }}
                >
                    Verify OTP
                </button>
                
            </>
            
        )}
        <button
            type="button"
            onClick={() => {
                setPhoneLogin(false);
                setPhoneOtpSent(false);
                setPhone("");
                setPhoneOtp("");
            }}
            className="text-sm text-[#1a6fa8] mb-4 hover:underline mt-10"
        >
            ← Back to Email Login
        </button>
    </div>
)}
                        {/* Divider */}
                        {loginRole === "patient" && !phoneLogin && (
                            <div className="flex items-center gap-3 my-6 text-slate-300 text-xs">
                                <div className="flex-1 h-px bg-slate-200" />
                                or sign in with email
                                <div className="flex-1 h-px bg-slate-200" />
                            </div>
                        )}
                        {!phoneLogin && (
                        <form onSubmit={handleLogin}>
                            {/* Email */}
                            <div className="mb-5">
                                <label className="block text-[11px] font-medium text-slate-500 uppercase tracking-wide mb-1.5">
                                    Email Address
                                </label>
                                <div className="relative">
                                    <span className={`absolute left-3.5 top-1/2 -translate-y-1/2 text-base pointer-events-none transition-colors duration-200 ${focused === "email" ? "text-[#1a6fa8]" : "text-slate-300"}`}>
                                        ✉
                                    </span>
                                    <input
                                        type="email"
                                        name="email"
                                        placeholder="you@example.com"
                                        value={formData.email}
                                        onChange={handleChange}
                                        onFocus={() => setFocused("email")}
                                        onBlur={() => setFocused("")}
                                        required
                                        className="auth-input font-dm w-full pl-10 pr-4 py-3 border-[1.5px] border-slate-200 rounded-xl text-sm bg-white text-slate-800 placeholder-slate-300 outline-none transition-all duration-200 focus:border-[#1a6fa8]"
                                    />
                                </div>
                            </div>

                            {/* Password */}
                            <div className="mb-3">
                                <label className="block text-[11px] font-medium text-slate-500 uppercase tracking-wide mb-1.5">
                                    Password
                                </label>

                                <div className="relative">
                                    {/* Lock Icon */}
                                    <span
                                        className={`absolute left-3.5 top-1/2 -translate-y-1/2 text-base pointer-events-none transition-colors duration-200 ${focused === "password" ? "text-[#1a6fa8]" : "text-slate-300"
                                            }`}
                                    >
                                        🔒
                                    </span>

                                    {/* Input */}
                                    <input
                                        type={showPassword ? "text" : "password"}
                                        name="password"
                                        placeholder="Enter your password"
                                        value={formData.password}
                                        onChange={handleChange}
                                        onFocus={() => setFocused("password")}
                                        onBlur={() => setFocused("")}
                                        required
                                        className="auth-input font-dm w-full pl-10 pr-10 py-3 border-[1.5px] border-slate-200 rounded-xl text-sm bg-white text-slate-800 placeholder-slate-300 outline-none transition-all duration-200 focus:border-[#1a6fa8]"
                                    />
                                    {/* Eye Icon */}
                                    <span
                                        onClick={() => setShowPassword(!showPassword)}
                                        className="absolute right-3.5 top-1/2 -translate-y-1/2 cursor-pointer text-lg text-slate-400 hover:text-[#1a6fa8]"
                                    >
                                        {showPassword ? <FaEyeSlash /> : <FaEye />}
                                    </span>
                                </div>
                            </div>
                            {/* Forgot */}
                            {loginRole === "patient" && !phoneLogin && (
                                <div className="text-right mb-7">
                                    <button
                                        type="button"
                                        onClick={() => navigate("/forgot-password")}
                                        className="text-[13px] text-[#1a6fa8] hover:underline bg-transparent border-none p-0 cursor-pointer"
                                    >
                                        Forgot password?
                                    </button>
                                </div>
                            )}
                            {/* Admin hint */}
                            {loginRole === "admin" && (
                                <div className="text-right mb-7">
                                    <span className="text-[12px] text-slate-400">🔐 Secure admin access via OTP</span>
                                </div>
                            )}


                            {(loginRole === "doctor" || loginRole === "admin") ? (
                                !otpSent ? (
                                    <button
                                        type="submit"
                                        disabled={loading}
                                        className="w-full py-3.5 rounded-xl text-white font-medium text-sm"
                                        style={{ background: "linear-gradient(135deg,#0a3d62,#1a6fa8)" }}
                                    >
                                        {loading ? "Sending OTP..." : "Send OTP"}
                                    </button>
                                ) : (
                                    <>
                                        {/* OTP Input */}
                                        <input
                                            type="text"
                                            placeholder="Enter 6-digit OTP"
                                            value={otp}
                                            onChange={(e) => setOtp(e.target.value)}
                                            maxLength={6}
                                            className="auth-input font-dm w-full mt-4 px-4 py-3 border-[1.5px] border-slate-200 rounded-xl text-sm bg-white text-slate-800 placeholder-slate-300 outline-none transition-all duration-200 focus:border-[#1a6fa8] tracking-[0.3em] text-center font-semibold"
                                        />

                                        {/* Verify Button */}
                                        <button
                                            type="button"
                                            onClick={handleVerifyOtp}
                                            disabled={loading}
                                            className="w-full mt-4 py-3.5 rounded-xl text-white font-medium text-sm"
                                            style={{ background: "linear-gradient(135deg,#0a3d62,#1a6fa8)" }}
                                        >
                                            {loading ? "Verifying..." : "Verify & Sign In"}
                                        </button>

                                        {/* Timer / Resend */}
                                        <div className="text-center mt-3 text-sm text-slate-500">
                                            {canResend ? (
                                                <button
                                                    type="button"
                                                    onClick={handleResendOtp}
                                                    className="text-[#1a6fa8] font-medium hover:underline"
                                                >
                                                    Resend OTP
                                                </button>
                                            ) : (
                                                <span>Resend OTP in {timer}s</span>
                                            )}
                                        </div>
                                    </>
                                )
                            ) : (
                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="w-full py-3.5 rounded-xl text-white font-medium text-sm"
                                    style={{ background: "linear-gradient(135deg,#0a3d62,#1a6fa8)" }}
                                >
                                    {loading ? "Signing in..." : "Sign In"}
                                </button>
                            )}
                                        </form>
                        )}
                       
                        {/* Divider */}
                        {loginRole === "patient" && (
                            <div className="flex items-center gap-3 my-6 text-slate-300 text-xs">
                                <div className="flex-1 h-px bg-slate-200" />
                                or
                                <div className="flex-1 h-px bg-slate-200" />
                            </div>
                        )}
                        {loginRole === "patient" && (
                            <p className="text-center text-sm text-slate-400">
                                Don't have an account?{" "}
                                <Link to="/signup" className="text-[#1a6fa8] font-medium hover:underline">Create one</Link>
                            </p>
                        )}
                        {/* Trust badges */}
                        <div className="flex justify-center gap-5 mt-8">
                            {["🔒 Secure", "🛡️ HIPAA", "✅ Verified Doctors"].map((item) => (
                                <div key={item} className="text-[11px] text-slate-300">{item}</div>
                            ))}
                        </div>


                    </div>
                </div>
            </div>

            <ToastContainer position="top-right" autoClose={2000} />
        </>
    );
}